import { token } from '@atlaskit/tokens';
import useThemeObserver from '@atlaskit/tokens/use-theme-observer';

import { type AgentBrandTone, getAgentBrandTone } from './agentBrandTone';
import type { PresenceHexOutline } from './PresenceHexArtwork';

export type { AgentBrandTone };
export { getAgentBrandTone };

export type AgentPresenceColorMode = 'light' | 'dark';

type FinalPresenceSize = 24 | 32;

type PresenceHexGeometry = {
	viewBox: string;
	hex: string;
	markOffset: { x: number; y: number };
};

type PresenceHexArt = {
	viewBox: string;
	hex: string;
	mark?: { x: number; y: number } & Record<string, unknown>;
};

const FINAL_PRESENCE_HEX_GEOMETRY: Record<FinalPresenceSize, PresenceHexGeometry> = {
	24: {
		viewBox: '0 1.3358 24 24',
		hex: 'M22.0557 5.35887L14.06 0.568665C12.7922 -0.190923 11.2087 -0.189455 9.94226 0.572483L1.93793 5.38806C0.735427 6.11151 0 7.41223 0 8.81558V17.8788C0 19.2848 0.738177 20.5876 1.94429 21.3101L9.94429 26.1029C11.2099 26.8611 12.7901 26.8611 14.0557 26.1029L22.0557 21.3101C23.2618 20.5876 24 19.2848 24 17.8788V8.7902C24 7.38421 23.2618 6.08145 22.0557 5.35887Z',
		markOffset: { x: -2, y: -2.0098 },
	},
	32: {
		viewBox: '0 0.7639 32 32',
		hex: 'M29.4485 6.81587L18.4537 0.64055C16.9302 -0.215083 15.0707 -0.213413 13.5488 0.644954L2.54367 6.85208C0.972102 7.73848 0 9.40283 0 11.2071V22.3496C0 24.1571 0.975536 25.8239 2.55149 26.709L13.5515 32.8872C15.0721 33.7413 16.9279 33.7413 18.4485 32.8872L29.4485 26.709C31.0245 25.8239 32 24.1571 32 22.3496V11.1753C32 9.3678 31.0245 7.70101 29.4485 6.81587Z',
		markOffset: { x: -1.0016, y: -1.4473 },
	},
};

const getFinalPresenceSize = (size: number): FinalPresenceSize | undefined => {
	if (size > 16 && size <= 24) {
		return 24;
	}
	if (size > 24 && size <= 32) {
		return 32;
	}
	return undefined;
};

export const isFinalPresenceSize = (size: number): boolean =>
	getFinalPresenceSize(size) !== undefined;

export const getFinalPresenceHexGeometry = (size: number): PresenceHexGeometry | undefined => {
	const finalSize = getFinalPresenceSize(size);
	return finalSize === undefined ? undefined : FINAL_PRESENCE_HEX_GEOMETRY[finalSize];
};

export const applyFinalPresenceHexGeometry = <T extends PresenceHexArt>(
	art: T,
	size: number,
): T => {
	const geometry = getFinalPresenceHexGeometry(size);
	if (!geometry) {
		return art;
	}
	return {
		...art,
		viewBox: geometry.viewBox,
		hex: geometry.hex,
		...(art.mark
			? {
					mark: {
						...art.mark,
						x: art.mark.x + geometry.markOffset.x,
						y: art.mark.y + geometry.markOffset.y,
					},
				}
			: {}),
	};
};

export const usePresenceColorMode = (): AgentPresenceColorMode => {
	const { colorMode } = useThemeObserver();
	return colorMode === 'dark' ? 'dark' : 'light';
};

/**
 * With the inset border on, an unset or `transparent` border asks for the tile's own border rather
 * than removing it, so callers that suppress the card-colored ring still get the contrast border.
 */
const getRequestedBorder = (borderColor: string | undefined): string | undefined =>
	borderColor === 'transparent' ? undefined : borderColor;

/** Whether the caller asked for no ring of their own, so the tile's own border applies. */
export const isBorderUnset = (borderColor: string | undefined): boolean =>
	getRequestedBorder(borderColor) === undefined;

export type PresenceChrome = {
	fill?: string;
	stroke?: string;
	strokeWidth?: number;
	insetStroke?: boolean;
};

/**
 * The inset border: a 1px stroke kept inside the hexagon so the tile's bounds are the same with
 * and without it. Only used with the inset border gate on.
 *
 * - `fill: 'surface'` repaints the tile with `elevation.surface` (white tiles); `'keep'` leaves it.
 * - `borderIn: 'both'` draws the border in light and dark mode; `'dark'` only in dark mode (black
 *   tiles, which only disappear against dark surfaces).
 *
 * An explicit visible `borderColor` is kept as the caller's ring instead.
 */
export const getInsetBorderChrome = ({
	fill,
	borderIn,
	colorMode = 'light',
	borderColor,
	defaultStrokeWidth,
}: {
	fill: 'surface' | 'keep';
	borderIn: 'both' | 'dark';
	colorMode?: AgentPresenceColorMode;
	borderColor?: string;
	defaultStrokeWidth?: number;
}): PresenceChrome => {
	const tileFill = fill === 'surface' ? token('elevation.surface') : undefined;
	const requestedBorder = getRequestedBorder(borderColor);
	if (requestedBorder !== undefined) {
		return { fill: tileFill, stroke: requestedBorder, strokeWidth: defaultStrokeWidth };
	}
	if (borderIn === 'dark' && colorMode !== 'dark') {
		return { fill: tileFill, stroke: 'transparent', strokeWidth: defaultStrokeWidth };
	}
	return {
		fill: tileFill,
		stroke: token('color.background.accent.gray.subtlest.hovered'),
		strokeWidth: 1,
		insetStroke: true,
	};
};

/**
 * A tile only loses contrast when its own tone matches the surface behind it, so the center stroke
 * is drawn on light tiles in light mode and dark tiles in dark mode, and never on both. `neutral`
 * tiles hold against either surface, and `adaptive` tiles invert to follow the surface, so neither
 * needs one.
 */
const needsCenterStroke = (tone: AgentBrandTone, colorMode: AgentPresenceColorMode): boolean =>
	tone === colorMode;

/**
 * The contrast treatment for a presence tile, as a {@link PresenceHexOutline} for
 * `PresenceHexOutlineContext`.
 *
 * `width` is zero because the outer ring is the caller's own `borderColor`; this only contributes
 * the center stroke.
 */
export const resolvePresenceOutline = ({
	tone,
	colorMode,
}: {
	tone: AgentBrandTone;
	colorMode: AgentPresenceColorMode;
}): PresenceHexOutline => ({
	width: 0,
	centerStrokeColor: needsCenterStroke(tone, colorMode)
		? token('color.background.accent.gray.subtler')
		: undefined,
	centerStrokeWidth: 1,
});

// TODO(teab_63_local_agent_borders cleanup): delete this helper; it only serves the gate-off path.
export const getWhiteFillPresenceChrome = ({
	size,
	colorMode,
	borderColor,
	defaultBorderColor,
	defaultStrokeWidth,
}: {
	size: number;
	colorMode: AgentPresenceColorMode;
	borderColor?: string;
	defaultBorderColor: string;
	defaultStrokeWidth?: number;
}): { fill: string; stroke?: string; strokeWidth?: number } => {
	if (!isFinalPresenceSize(size)) {
		return {
			fill: 'white',
			stroke: borderColor ?? defaultBorderColor,
			strokeWidth: defaultStrokeWidth,
		};
	}
	const stroke =
		borderColor ??
		(colorMode === 'light' ? token('color.background.accent.gray.subtler') : undefined);
	return {
		fill: 'white',
		stroke,
		strokeWidth: stroke === undefined ? undefined : 1,
	};
};
